import { CircularProgress, Icon, IconButton, Paper, Popover } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import { useEffect, useState, useMemo, useCallback } from "react";
import { useDateRangeContext } from "modules/jobs/JobsSchedule/DateRangeContext";
import useGetAllCrews from "hooks/crews/useGetAllCrews";
import useGetAllOrganizationUsers from "hooks/users/useGetAllOrganizationUsers";
import ScheduleFilters from "modules/jobs/JobsSchedule/ScheduleFilters";
import useModalState from "hooks/useModalState";
import Modal from "modules/Modal/Modal";
import CreateUpdateCustomEvent from "modules/CustomEvent/CreateUpdateCustomEvent";
import JobScheduleStats from "modules/jobs/JobScheduleStats/JobScheduleStats";
import JobsSchedulesCalendar from "modules/jobs/JobsSchedulesCalendar/JobsSchedulesCalendar";

function JobScheduleStatsWithContext({ jobId }: { jobId: string | null }) {
  const { startDate, endDate } = useDateRangeContext();
  return <JobScheduleStats startDate={startDate} endDate={endDate} jobId={jobId} compact={true} />;
}

export default function JobsScheduleCalendarOnly({
  view: _view,
  onViewChange: _onViewChange,
}: {
  view: "calendar" | "map";
  onViewChange: (view: "calendar" | "map") => void;
}) {
  const { data: crews, loading } = useGetAllCrews();
  const [checked, setChecked] = useState(() => {
    if (!crews) return {};
    return crews.reduce((acc, val) => {
      return {
        ...acc,
        [val.id]: {
          ...val,
          checked: true,
        },
      };
    }, {});
  });
  const [showCustom, setShowCustom] = useState(true);
  const { open, onClose, onOpen } = useModalState();
  const [selectedProjectManagers, setSelectedProjectManagers] = useState([]);
  const [filtersAnchorEl, setFiltersAnchorEl] = useState<HTMLElement | null>(null);
  const { data: organizationUsers } = useGetAllOrganizationUsers();

  const projectManagerOptions = useMemo(() => {
    if (!organizationUsers) return [];
    return organizationUsers.map((user) => ({
      label: `${user.firstName} ${user.lastName}`,
      value: user.id,
    }));
  }, [organizationUsers]);

  const handleCrewCheckedChanged = useCallback(
    (crew: any, isChecked: boolean) => {
      setChecked((prevValues: any) => ({
        ...prevValues,
        [crew.value]: {
          ...prevValues[crew.value],
          checked: isChecked,
        },
      }));
    },
    [setChecked]
  );

  const crewOptions = useMemo(() => {
    if (!crews) {
      return [];
    }
    return crews.map((crew) => {
      return {
        label: crew.name,
        value: crew.id,
      };
    });
  }, [crews]);

  const handleCrewFilterChange = useCallback(
    (selectedValues: string[]) => {
      crewOptions.forEach((crew) => {
        handleCrewCheckedChanged(crew, selectedValues.includes(crew.value));
      });
    },
    [crewOptions, handleCrewCheckedChanged]
  );

  const handleProjectManagerFilterChange = useCallback((selectedValues: string[]) => {
    setSelectedProjectManagers(selectedValues as any);
  }, []);

  useEffect(() => {
    if (crews) {
      setChecked(
        crews.reduce((acc, val) => {
          return {
            ...acc,
            [val.id]: {
              ...val,
              checked: true,
            },
          };
        }, {})
      );
    }
  }, [crews]);

  if (loading) {
    return <CircularProgress color="inherit" />;
  }

  if (!crews) {
    return null;
  }

  return (
    <MDBox
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        width: "100%",
        position: "relative",
      }}
    >
      <Paper
        elevation={0}
        sx={{
          position: "absolute",
          top: 12,
          right: 16,
          zIndex: 5,
          px: 1.25,
          py: 1,
          borderRadius: "12px",
          border: "1px solid",
          borderColor: "grey.200",
          backgroundColor: "rgba(255,255,255,0.92)",
          backdropFilter: "blur(8px)",
        }}
      >
        <JobScheduleStatsWithContext jobId={null} />
      </Paper>

      <MDBox
        mt={2}
        sx={{
          p: 2,
          borderBottom: "1px solid #e5e7eb",
          flexShrink: 0,
          display: "flex",
          gap: 1,
          alignItems: "center",
          pr: { xs: 2, lg: 34 },
          flexWrap: "wrap",
        }}
      >
        <IconButton
          onClick={(event) => setFiltersAnchorEl(event.currentTarget)}
          sx={{
            width: 36,
            height: 36,
            border: "1px solid",
            borderColor: "grey.300",
            borderRadius: "10px",
            backgroundColor: "white",
          }}
        >
          <Icon sx={{ fontSize: "18px!important" }}>filter_list</Icon>
        </IconButton>

        <MDButton
          className="tour-add-event"
          color="dark"
          onClick={onOpen}
          sx={{
            minWidth: "auto",
            px: 1.5,
            py: 1,
            fontSize: "0.75rem",
            lineHeight: 1,
            whiteSpace: "nowrap",
          }}
        >
          + Event
        </MDButton>
      </MDBox>

      <Popover
        open={!!filtersAnchorEl}
        anchorEl={filtersAnchorEl}
        onClose={() => setFiltersAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            mt: 1,
            p: 2,
            borderRadius: "14px",
            border: "1px solid",
            borderColor: "grey.200",
            backgroundColor: "#f7f9fa",
          },
        }}
      >
        <ScheduleFilters
          checked={checked}
          crewOptions={crewOptions}
          projectManagerOptions={projectManagerOptions}
          selectedProjectManagers={selectedProjectManagers}
          onCrewChange={handleCrewFilterChange}
          onProjectManagerChange={handleProjectManagerFilterChange}
        />
      </Popover>

      <MDBox
        sx={{
          flex: 1,
          width: "100%",
          overflow: "auto",
          backgroundColor: "white!important",
        }}
      >
        <JobsSchedulesCalendar
          showCustomEvents={showCustom}
          jobIds={null}
          crewIds={Object.keys(checked).filter((key) => (checked as any)[key].checked)}
          projectManagerIds={selectedProjectManagers}
          compactToolbar={true}
          useViewMenu={true}
          calendarHeight="auto"
        />
      </MDBox>

      <Modal open={open} onClose={onClose}>
        <CreateUpdateCustomEvent action="create" onClose={onClose} />
      </Modal>
    </MDBox>
  );
}
